import { create } from 'zustand';
import { api } from '../services/api';

export interface BudgetAccount {
  id: string;
  family_id: string;
  name: string;
  account_type: string;
  balance_pence: number;
  currency: string;
  colour: string;
  created_by: string;
  created_at: string;
}

export interface Transaction {
  id: string;
  family_id: string;
  account_id: string;
  amount_pence: number;
  transaction_type: string;
  category: string;
  description: string;
  date: string;
  added_by: string;
  added_by_name: string;
  receipt_url: string;
  created_at: string;
}

export interface Bill {
  id: string;
  family_id: string;
  name: string;
  category: string;
  amount_pence: number;
  due_day: number;
  frequency: string;
  next_due_date: string;
  auto_renews: boolean;
  provider: string;
  notes: string;
  created_by: string;
  created_at: string;
}

export interface SpendingTrend {
  month: string;
  income_pence: number;
  expense_pence: number;
}

export interface ReceiptResult {
  store: string;
  total_pence: number;
  items: { name: string; price_pence: number }[];
  source: string;
}

export interface StatementBill {
  name: string;
  amount_pence: number;
  frequency: string;
  category: string;
}

export interface ImportedTransaction {
  id: string;
  date: string;
  description: string;
  amount_pence: number;
  transaction_type: 'income' | 'expense';
  category: string;
  is_duplicate: boolean;
}

export interface ImportStatementPreview {
  format: string;
  confidence: number;
  transactions: ImportedTransaction[];
  duplicate_count: number;
}

interface BudgetState {
  accounts: BudgetAccount[];
  transactions: Transaction[];
  bills: Bill[];
  summary: Record<string, any> | null;
  spendingTrends: SpendingTrend[];
  monthlyBudgetPence: number;
  isLoading: boolean;
  error: string | null;
  fetchAccounts: () => Promise<void>;
  createAccount: (data: Partial<BudgetAccount>) => Promise<BudgetAccount>;
  updateAccount: (id: string, data: Partial<BudgetAccount>) => Promise<void>;
  deleteAccount: (id: string) => Promise<void>;
  fetchTransactions: (filters?: { month?: string; category?: string; type?: string }) => Promise<void>;
  createTransaction: (data: Partial<Transaction>) => Promise<Transaction>;
  deleteTransaction: (id: string) => Promise<void>;
  fetchBills: () => Promise<void>;
  createBill: (data: Partial<Bill>) => Promise<Bill>;
  updateBill: (id: string, data: Partial<Bill>) => Promise<void>;
  deleteBill: (id: string) => Promise<void>;
  markBillPaid: (id: string) => Promise<{ transaction: Transaction; bill: Bill }>;
  fetchSummary: (month?: string) => Promise<void>;
  fetchSpendingTrends: () => Promise<void>;
  fetchMonthlyBudget: () => Promise<void>;
  setMonthlyBudget: (amount_pence: number) => Promise<void>;
  scanReceipt: (image: string, mimeType?: string) => Promise<ReceiptResult>;
  scanStatement: (image: string, mimeType?: string) => Promise<StatementBill[]>;
  importStatementParse: (data: string, mimeType: string, filename: string) => Promise<ImportStatementPreview>;
  importStatementCommit: (transactions: Partial<ImportedTransaction>[], accountId?: string) => Promise<number>;
  getBankConnectUrl: () => Promise<string | null>;
  fetchCategorySummary: () => Promise<Array<{ category: string; tx_count: number; month_spend_pence: number }>>;
  renameCategory: (from: string, to: string) => Promise<void>;
  mergeCategoryIntoOther: (name: string) => Promise<void>;
  // WebSocket handlers
  handleWsMessage: (message: any) => void;
}

export const useBudgetStore = create<BudgetState>((set, get) => ({
  accounts: [],
  transactions: [],
  bills: [],
  summary: null,
  spendingTrends: [],
  monthlyBudgetPence: 0,
  isLoading: false,
  error: null,

  fetchAccounts: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get('/budget/accounts');
      // Ensure balance_pence is numeric (PostgreSQL may return strings)
      const accounts = (res.data.accounts || []).map((a: any) => ({
        ...a,
        balance_pence: Number(a.balance_pence) || 0,
      }));
      set({ accounts, isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  createAccount: async (data) => {
    try {
      const res = await api.post('/budget/accounts', data);
      const newAccount = res.data.account;
      set(state => {
        const exists = state.accounts.find(a => a.id === newAccount.id);
        if (exists) return state;
        return { accounts: [newAccount, ...state.accounts] };
      });
      return newAccount;
    } catch (err: any) {
      set({ error: err.response?.data?.error || err.message });
      throw err;
    }
  },

  updateAccount: async (id, data) => {
    try {
      const res = await api.put(`/budget/accounts/${id}`, data);
      const updatedAccount = res.data.account;
      set(state => ({
        accounts: state.accounts.map(a => a.id === id ? updatedAccount : a),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  deleteAccount: async (id) => {
    try {
      await api.delete(`/budget/accounts/${id}`);
      set(state => ({
        accounts: state.accounts.filter(a => a.id !== id),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  fetchTransactions: async (filters?) => {
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams();
      if (filters?.month) params.append('month', filters.month);
      if (filters?.category) params.append('category', filters.category);
      if (filters?.type) params.append('type', filters.type);
      const query = params.toString() ? `?${params.toString()}` : '';
      const res = await api.get(`/budget/transactions${query}`);
      // Ensure amount_pence is numeric (PostgreSQL may return strings)
      const transactions = (res.data.transactions || []).map((t: any) => ({
        ...t,
        amount_pence: Number(t.amount_pence) || 0,
      }));
      set({ transactions, isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  createTransaction: async (data) => {
    try {
      const res = await api.post('/budget/transactions', data);
      const newTransaction = res.data.transaction;
      set(state => {
        if (state.transactions.some(t => t.id === newTransaction.id)) return state;
        return { transactions: [newTransaction, ...state.transactions] };
      });
      return newTransaction;
    } catch (err: any) {
      set({ error: err.response?.data?.error || err.message });
      throw err;
    }
  },

  deleteTransaction: async (id) => {
    await api.delete(`/budget/transactions/${id}`);
    set(state => ({
      transactions: state.transactions.filter(t => t.id !== id),
    }));
  },

  fetchBills: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get('/budget/bills');
      set({ bills: res.data.bills, isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  createBill: async (data) => {
    try {
      const res = await api.post('/budget/bills', data);
      const newBill = res.data.bill;
      set(state => ({ bills: [newBill, ...state.bills] }));
      return newBill;
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  updateBill: async (id, data) => {
    try {
      const res = await api.put(`/budget/bills/${id}`, data);
      const updatedBill = res.data.bill;
      set(state => ({
        bills: state.bills.map(b => b.id === id ? updatedBill : b),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  deleteBill: async (id) => {
    try {
      await api.delete(`/budget/bills/${id}`);
      set(state => ({
        bills: state.bills.filter(b => b.id !== id),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  fetchSummary: async (month?) => {
    set({ isLoading: true, error: null });
    try {
      const query = month ? `?month=${month}` : '';
      const res = await api.get(`/budget/summary${query}`);
      const summary = res.data.summary || res.data;
      const updates: Partial<BudgetState> = { summary, isLoading: false };
      if (summary && typeof summary.budget_pence === 'number') {
        updates.monthlyBudgetPence = summary.budget_pence;
      }
      set(updates);
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  fetchMonthlyBudget: async () => {
    try {
      const res = await api.get('/budget/budget');
      set({ monthlyBudgetPence: Number(res.data.budget_pence) || 0 });
    } catch {
      // Silently ignore — the monthly budget is an optional feature, and a
      // missing endpoint (e.g. an older backend) shouldn't surface as a
      // generic "Network Error" on the Budget page. fetchSummary also
      // populates monthlyBudgetPence when the backend is up to date.
    }
  },

  setMonthlyBudget: async (amount_pence) => {
    try {
      const res = await api.post('/budget/set-budget', { amount_pence });
      set({ monthlyBudgetPence: Number(res.data.budget_pence) || amount_pence });
    } catch (err: any) {
      set({ error: err.response?.data?.error || err.message });
      throw err;
    }
  },

  fetchSpendingTrends: async () => {
    try {
      const res = await api.get('/budget/spending-trends');
      set({ spendingTrends: res.data.trends });
    } catch (err: any) {
      set({ error: err.message });
    }
  },

  markBillPaid: async (id) => {
    const res = await api.post(`/budget/bills/${id}/pay`);
    const { transaction, bill } = res.data;
    set(state => ({
      transactions: [{ ...transaction, amount_pence: Number(transaction.amount_pence) || 0 }, ...state.transactions],
      bills: state.bills.map(b => b.id === id ? { ...b, ...bill } : b),
    }));
    get().fetchSummary();
    return { transaction, bill };
  },

  scanReceipt: async (image, mimeType) => {
    const res = await api.post('/budget/receipt', { image, mimeType }, { timeout: 60000 });
    return res.data as ReceiptResult;
  },

  scanStatement: async (image, mimeType) => {
    const res = await api.post('/budget/scan-statement', { image, mimeType }, { timeout: 60000 });
    return (res.data.bills || []) as StatementBill[];
  },

  importStatementParse: async (data, mimeType, filename) => {
    // Parsing PDFs and large CSVs on the backend can easily take longer
    // than the default 10-second axios timeout — especially when the
    // Claude text fallback kicks in.
    const res = await api.post(
      '/budget/import-statement/parse',
      { data, mimeType, filename },
      {
        timeout: 180000,
        maxBodyLength: 20 * 1024 * 1024,
        maxContentLength: 20 * 1024 * 1024,
      }
    );
    return res.data as ImportStatementPreview;
  },

  importStatementCommit: async (transactions, accountId) => {
    const res = await api.post(
      '/budget/import-statement/commit',
      { transactions, account_id: accountId },
      { timeout: 120000 }
    );
    const created = (res.data.transactions || []).map((t: any) => ({
      ...t,
      amount_pence: Number(t.amount_pence) || 0,
    }));
    // Optimistic update — the WS TRANSACTION_CREATED events will also
    // arrive but adding them here keeps the UI snappy.
    set(state => ({ transactions: [...created, ...state.transactions] }));
    return res.data.imported_count || created.length;
  },

  getBankConnectUrl: async () => {
    try {
      const statusRes = await api.get('/budget/bank/status');
      if (!statusRes.data.configured) return null;
      const res = await api.get('/budget/bank/connect');
      return res.data.auth_url || null;
    } catch {
      return null;
    }
  },

  fetchCategorySummary: async () => {
    try {
      const res = await api.get('/budget/categories');
      return res.data.categories || [];
    } catch {
      return [];
    }
  },

  renameCategory: async (from, to) => {
    await api.put('/budget/categories/rename', { from, to });
    await get().fetchTransactions();
    await get().fetchBills();
  },

  mergeCategoryIntoOther: async (name) => {
    await api.delete(`/budget/categories/${encodeURIComponent(name)}`);
    await get().fetchTransactions();
    await get().fetchBills();
  },

  handleWsMessage: (message) => {
    switch (message.type) {
      case 'ACCOUNT_CREATED':
        set(state => {
          const exists = state.accounts.find(a => a.id === message.payload.id);
          if (exists) return state;
          return { accounts: [message.payload, ...state.accounts] };
        });
        break;

      case 'ACCOUNT_UPDATED':
        set(state => ({
          accounts: state.accounts.map(a =>
            a.id === message.payload.id ? { ...a, ...message.payload } : a
          ),
        }));
        break;

      case 'ACCOUNT_DELETED':
        set(state => ({
          accounts: state.accounts.filter(a => a.id !== message.payload.id),
        }));
        break;

      case 'TRANSACTION_CREATED':
        set(state => {
          const exists = state.transactions.find(t => t.id === message.payload.id);
          if (exists) return state;
          return { transactions: [{ ...message.payload, amount_pence: Number(message.payload.amount_pence) || 0 }, ...state.transactions] };
        });
        get().fetchSummary();
        break;

      case 'TRANSACTION_UPDATED':
        set(state => ({
          transactions: state.transactions.map(t =>
            t.id === message.payload.id ? { ...t, ...message.payload, amount_pence: Number(message.payload.amount_pence) || 0 } : t
          ),
        }));
        get().fetchSummary();
        break;

      case 'TRANSACTION_DELETED':
        set(state => ({
          transactions: state.transactions.filter(t => t.id !== message.payload.id),
        }));
        get().fetchSummary();
        break;

      case 'BILL_CREATED':
        set(state => {
          const exists = state.bills.find(b => b.id === message.payload.id);
          if (exists) return state;
          return { bills: [message.payload, ...state.bills] };
        });
        break;

      case 'BILL_UPDATED':
        set(state => ({
          bills: state.bills.map(b =>
            b.id === message.payload.id ? { ...b, ...message.payload } : b
          ),
        }));
        break;

      case 'BILL_DELETED':
        set(state => ({
          bills: state.bills.filter(b => b.id !== message.payload.id),
        }));
        break;

      case 'SUMMARY_UPDATED':
        set({ summary: message.payload });
        break;

      case 'BUDGET_UPDATED':
        if (message.payload && typeof message.payload.budget_pence === 'number') {
          set({ monthlyBudgetPence: Number(message.payload.budget_pence) });
        }
        break;
    }
  },
}));
